import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Image as ImageIcon, Loader2, MessageSquare, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getJson, postForm } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { AuthorLink, Avatar, FORUM_CATEGORIES, JoinPrompt, PendingNote, PhotoPicker, useTimeAgo } from "./shared";

const NewTopic = ({ open, setOpen, category }) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [v, setV] = useState({ category: category || "general", title: "", body: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const tp = await postForm("/community/topics", { ...v, file });
      if (tp.status === "published") navigate(`/comunidad/tema/${tp.id}`);
      else setPending(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg border-white/10 bg-[#15130f] text-[#efe8dc]" data-testid="new-topic-dialog" data-lenis-prevent>
        <DialogTitle className="font-display text-3xl font-normal">{t("com.newTopic")}</DialogTitle>
        <form onSubmit={submit} className="mt-2 space-y-3" data-testid="new-topic-form">
          <select value={v.category} onChange={set("category")} className="support-input" data-testid="new-topic-category">
            {FORUM_CATEGORIES.map((c) => <option key={c} value={c}>{t(`fcat.${c}`)}</option>)}
          </select>
          <input value={v.title} onChange={set("title")} required minLength={4} maxLength={140} placeholder={t("com.titlePh")} className="support-input" data-testid="new-topic-title" />
          <textarea value={v.body} onChange={set("body")} required maxLength={5000} rows={6} placeholder={t("com.bodyPh")} className="support-input resize-none" data-testid="new-topic-body" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <PhotoPicker file={file} setFile={setFile} testid="new-topic-photo" />
            <button type="submit" disabled={busy} className="btn-primary" data-testid="new-topic-submit">{busy && <Loader2 size={15} className="animate-spin" />} {t("com.publish")}</button>
          </div>
          <PendingNote show={pending} />
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const TopicRow = ({ tp }) => {
  const { t } = useLang();
  const ago = useTimeAgo();
  return (
    <div className="group relative flex items-start gap-4 border-b border-white/10 py-5 transition-colors hover:bg-white/[0.02]" data-testid={`topic-row-${tp.id}`}>
      <AuthorLink author={tp.author} className="relative z-10 shrink-0"><Avatar author={tp.author} size={40} /></AuthorLink>
      <div className="min-w-0 flex-1">
        <p className="mono text-[11px] uppercase tracking-[0.16em] text-[var(--green)]">{t(`fcat.${tp.category}`)}</p>
        <h3 className="font-display mt-1 text-2xl leading-tight transition-colors group-hover:text-[var(--green)]">
          <Link to={`/comunidad/tema/${tp.id}`} className="after:absolute after:inset-0" data-testid={`topic-link-${tp.id}`}>{tp.title}</Link>
        </h3>
        <p className="mt-1 line-clamp-1 text-sm text-[var(--muted)]">{tp.body}</p>
        <p className="mono mt-2 text-[11px] text-[var(--muted)]">
          <AuthorLink author={tp.author} className="relative z-10 hover:text-[var(--green)] hover:underline" testid={`topic-row-author-${tp.id}`}>{tp.author?.name}</AuthorLink> · {ago(tp.last_activity || tp.created_at)}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2 text-[var(--muted)]">
        <span className="inline-flex items-center gap-1 text-sm" data-testid={`topic-replies-${tp.id}`}><MessageSquare size={14} /> {tp.reply_count ?? 0}</span>
        {tp.image_url && <ImageIcon size={14} />}
      </div>
    </div>
  );
};

export const Forums = () => {
  const { t } = useLang();
  const { member } = useAuth();
  const [cat, setCat] = useState("");
  const [topics, setTopics] = useState(null);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  useEffect(() => {
    getJson("/community/stats").then((d) => setStats(d?.categories ?? {}));
  }, []);
  useEffect(() => {
    setTopics(null);
    getJson(`/community/topics${cat ? `?category=${cat}` : ""}`).then((d) => setTopics(Array.isArray(d) ? d : []));
  }, [cat]);
  return (
    <div className="grid gap-10 lg:grid-cols-[240px_1fr]" data-testid="community-forums">
      <aside>
        <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible" data-testid="forum-categories">
          {["", ...FORUM_CATEGORIES].map((c) => (
            <button key={c || "all"} type="button" onClick={() => setCat(c)} className={`bk-filter shrink-0 ${cat === c ? "bk-filter-on" : ""}`} data-testid={`forum-cat-${c || "all"}`}>
              <span>{c ? t(`fcat.${c}`) : t("col.allU")}</span>
              {c && <span className="mono text-[11px]">{stats[c] ?? 0}</span>}
            </button>
          ))}
        </div>
      </aside>
      <section>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="font-display text-3xl">{cat ? t(`fcat.${cat}`) : t("com.allTopics")}</h2>
          {member && <button type="button" onClick={() => setOpen(true)} className="btn-primary" data-testid="new-topic-btn"><Plus size={16} /> {t("com.newTopic")}</button>}
        </div>
        {!member && <div className="mt-6"><JoinPrompt next="/comunidad" /></div>}
        <div className="mt-4">
          {topics === null && <Loader2 className="mx-auto mt-12 animate-spin" />}
          {topics?.length === 0 && <p className="py-12 text-center text-[var(--muted)]" data-testid="forum-empty">{t("com.noTopics")}</p>}
          {topics?.map((tp) => <TopicRow key={tp.id} tp={tp} />)}
        </div>
      </section>
      {open && <NewTopic open={open} setOpen={setOpen} category={cat} />}
    </div>
  );
};
